import { useCallback, useRef, useState } from "react"
import { UseFormReturn } from "react-hook-form"
import type { ApplicableBetterDocUsage } from "@/modules/patients/detail/better-doc/hooks/useApplicableBetterDocUsage"
import { isBetterDocAvailable } from "@/modules/patients/detail/better-doc/lib/is-better-doc-available"
import { isBetterDocRequired } from "@/modules/patients/detail/better-doc/lib/is-better-doc-required"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import { useConfirmBetterDocRejectionMutation } from "@/modules/patients/services/useConfirmBetterDocRejectionMutation"
import { BetterDocRejectionReason, Country } from "@/types/__generated__/union-types"

type UseBetterDocRejectionConfirmationParameters = {
  form: UseFormReturn<PointOfCareFormType>
  countryCode: Country
  progressNoteId: string
  confirmedBetterDocRejectionReason: BetterDocRejectionReason | null | undefined
}

export type BetterDocCheck = (
  applicableBetterDocUsage: ApplicableBetterDocUsage,
  callback: VoidFunction,
  event: React.FocusEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
) => void

type UseBetterDocRejectionConfirmationResult = {
  onBetterDocCheck: BetterDocCheck
  isRejectionDialogOpen: boolean
  isConfirmingBetterDocRejection: boolean
  handleRejectionCancel: () => void
  handleRejectionConfirm: (betterDocRejectionReason: BetterDocRejectionReason) => void
  handleRejectionDialogCloseAutoFocus: (event: Event) => void
}

export const useBetterDocRejectionConfirmation = ({
  form,
  countryCode,
  progressNoteId,
  confirmedBetterDocRejectionReason,
}: UseBetterDocRejectionConfirmationParameters): UseBetterDocRejectionConfirmationResult => {
  const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState(false)
  const careProviderFieldRef = useRef<HTMLElement | null>(null)
  const openCareProviderSearchRef = useRef<VoidFunction | null>(null)

  const { getValues } = form
  const { confirmBetterDocRejection, isConfirmingBetterDocRejection } =
    useConfirmBetterDocRejectionMutation(countryCode)
  const isBetterDocApplicable = isBetterDocAvailable(countryCode)

  const onBetterDocCheck = useCallback<BetterDocCheck>(
    (applicableBetterDocUsage, callback, event): void => {
      if (isBetterDocApplicable && applicableBetterDocUsage.isBetterDocUsagePending) {
        event.preventDefault()
        const { currentTarget } = event
        currentTarget.blur()
        return
      }

      const isRejectionReasonMissing =
        !confirmedBetterDocRejectionReason && getValues("betterDocSuggestionStatus") !== "MANUAL_SEARCH_NEEDED"
      if (
        isBetterDocApplicable &&
        isBetterDocRequired(applicableBetterDocUsage.betterDocUsage) &&
        isRejectionReasonMissing
      ) {
        event.preventDefault()
        const { currentTarget } = event
        currentTarget.blur()

        careProviderFieldRef.current = currentTarget
        openCareProviderSearchRef.current = callback
        setIsRejectionDialogOpen(true)
      } else {
        callback()
      }
    },
    [confirmedBetterDocRejectionReason, getValues, isBetterDocApplicable],
  )

  const handleRejectionCancel = useCallback((): void => {
    setIsRejectionDialogOpen(false)
  }, [])

  const handleRejectionConfirm = useCallback(
    (selectedBetterDocRejectionReason: BetterDocRejectionReason): void => {
      confirmBetterDocRejection(
        { id: progressNoteId, betterDocRejectionReason: selectedBetterDocRejectionReason },
        () => {
          setIsRejectionDialogOpen(false)
        },
      )
    },
    [confirmBetterDocRejection, progressNoteId],
  )

  const handleRejectionDialogCloseAutoFocus = useCallback(
    (event: Event): void => {
      event.preventDefault()
      if (confirmedBetterDocRejectionReason) {
        careProviderFieldRef.current?.focus()
        openCareProviderSearchRef.current?.()
      } else {
        document.getElementById("better-doc-button")?.focus()
      }
    },
    [confirmedBetterDocRejectionReason],
  )

  return {
    onBetterDocCheck,
    isRejectionDialogOpen,
    isConfirmingBetterDocRejection,
    handleRejectionCancel,
    handleRejectionConfirm,
    handleRejectionDialogCloseAutoFocus,
  }
}
